/**
 * On-screen debug log — the ring of things that went wrong in THIS tab, kept
 * so a user can read it (features/debug/DebugLogPanel.tsx) and send it to the
 * project owner (POST /api/debug-reports) without opening DevTools.
 *
 * What lands here:
 *  - uncaught errors and unhandled promise rejections (`installDebugCapture`)
 *  - everything written to console.error / console.warn (same)
 *  - every failed /api call (core/api.ts) and session-cookie mint (auth-session)
 *  - every collaboration-socket transition: connect, loss, close code, the
 *    deploy handover, a refused board, the offline freeze (core/session.ts)
 *
 * Bounded by the SHARED caps (@kola/shared): the ring holds at most
 * DEBUG_REPORT_MAX_ENTRIES and every string is clipped to what the server's
 * schema accepts, so a report built from it is never refused for length.
 * A repeat of the last line (a socket retrying every few seconds) bumps a
 * count instead of appending, so a long outage reads as ONE line "×40"
 * rather than pushing the cause out of the ring.
 *
 * No React, no DOM at module scope: the store is plain zustand/vanilla so it
 * can be imported by core modules (api.ts, session.ts) and tested in node.
 */
import {
  DEBUG_REPORT_MAX_DETAIL,
  DEBUG_REPORT_MAX_ENTRIES,
  DEBUG_REPORT_MAX_MESSAGE,
  DEBUG_REPORT_MAX_SOURCE,
  type DebugLogEntryDto,
  type DebugLogLevel,
} from "@kola/shared";
import { createStore } from "zustand/vanilla";

export interface DebugLogEntry extends DebugLogEntryDto {
  /** Stable key for rendering. */
  id: number;
  /** How many consecutive identical lines this entry stands for (≥ 1). */
  count: number;
}

export interface DebugLogState {
  /** Oldest first. */
  entries: DebugLogEntry[];
  /** Errors logged while the panel was closed — the badge count. */
  unseen: number;
  open: boolean;
}

export const debugLogStore = createStore<DebugLogState>(() => ({
  entries: [],
  unseen: 0,
  open: false,
}));

/** Collaboration socket status of the board on screen, for the report context. */
let connectionStatus: string | null = null;

export function setDebugConnection(status: string | null): void {
  connectionStatus = status;
}

export function debugConnection(): string | null {
  return connectionStatus;
}

let nextId = 1;

function clip(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

export function logDebug(
  level: DebugLogLevel,
  source: string,
  message: string,
  detail?: string,
): void {
  const clippedMessage = clip(message, DEBUG_REPORT_MAX_MESSAGE);
  const clippedDetail = detail === undefined ? undefined : clip(detail, DEBUG_REPORT_MAX_DETAIL);
  const clippedSource = clip(source, DEBUG_REPORT_MAX_SOURCE);
  const ts = Date.now();

  debugLogStore.setState((state) => {
    const last = state.entries[state.entries.length - 1];
    const unseen = level === "error" && !state.open ? state.unseen + 1 : state.unseen;
    if (
      last !== undefined &&
      last.level === level &&
      last.source === clippedSource &&
      last.message === clippedMessage &&
      last.detail === clippedDetail
    ) {
      const merged: DebugLogEntry = { ...last, ts, count: last.count + 1 };
      return { entries: [...state.entries.slice(0, -1), merged], unseen };
    }
    const entry: DebugLogEntry = {
      id: nextId++,
      ts,
      level,
      source: clippedSource,
      message: clippedMessage,
      ...(clippedDetail === undefined ? {} : { detail: clippedDetail }),
      count: 1,
    };
    const entries =
      state.entries.length >= DEBUG_REPORT_MAX_ENTRIES
        ? [...state.entries.slice(state.entries.length - DEBUG_REPORT_MAX_ENTRIES + 1), entry]
        : [...state.entries, entry];
    return { entries, unseen };
  });
}

export const debugLog = {
  error: (source: string, message: string, detail?: string) =>
    logDebug("error", source, message, detail),
  warn: (source: string, message: string, detail?: string) =>
    logDebug("warn", source, message, detail),
  info: (source: string, message: string, detail?: string) =>
    logDebug("info", source, message, detail),
};

/** Open/close the panel. Opening marks every error seen. */
export function openDebugLog(open: boolean): void {
  debugLogStore.setState((state) => ({ open, unseen: open ? 0 : state.unseen }));
}

export function clearDebugLog(): void {
  debugLogStore.setState({ entries: [], unseen: 0 });
}

/** Message + detail for anything a catch block or an event hands us. */
export function describeError(value: unknown): { message: string; detail?: string } {
  if (value instanceof Error) {
    const message = value.name && value.name !== "Error" ? `${value.name}: ${value.message}` : value.message;
    const stack = value.stack ?? "";
    // The first stack line repeats the message; keep only the frames.
    const frames = stack.startsWith(message) ? stack.slice(message.length).trim() : stack;
    return frames ? { message, detail: frames } : { message };
  }
  if (typeof value === "string") return { message: value };
  try {
    return { message: JSON.stringify(value) ?? String(value) };
  } catch {
    return { message: String(value) };
  }
}

/** Render console.* arguments the way DevTools would, without the objects. */
export function formatConsoleArgs(args: readonly unknown[]): { message: string; detail?: string } {
  const parts: string[] = [];
  const details: string[] = [];
  for (const arg of args) {
    if (arg instanceof Error) {
      const described = describeError(arg);
      parts.push(described.message);
      if (described.detail !== undefined) details.push(described.detail);
    } else if (typeof arg === "string") {
      parts.push(arg);
    } else {
      try {
        parts.push(JSON.stringify(arg) ?? String(arg));
      } catch {
        parts.push(String(arg));
      }
    }
  }
  const message = parts.join(" ") || "(empty)";
  return details.length > 0 ? { message, detail: details.join("\n") } : { message };
}

/** The pieces of the environment `installDebugCapture` hooks — injectable for tests. */
export interface DebugCaptureTarget {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
  console: Pick<Console, "error" | "warn">;
}

let uninstall: (() => void) | null = null;

/**
 * Hook the global error sources. Idempotent: a second call is a no-op until
 * the returned uninstaller has run. Call this BEFORE React mounts (main.tsx)
 * so a bootstrap failure is the first line in the log, not the one it misses.
 */
export function installDebugCapture(
  target: DebugCaptureTarget = window as unknown as DebugCaptureTarget,
): () => void {
  if (uninstall !== null) return uninstall;

  const onError = (event: unknown) => {
    const e = event as { message?: string; error?: unknown; filename?: string; lineno?: number };
    const described = e.error !== undefined && e.error !== null ? describeError(e.error) : { message: e.message ?? "Unknown error" };
    const where = e.filename ? ` (${e.filename}:${e.lineno ?? 0})` : "";
    debugLog.error("window", `${described.message}${where}`, described.detail);
  };
  const onRejection = (event: unknown) => {
    const { reason } = event as { reason?: unknown };
    const described = describeError(reason);
    debugLog.error("promise", `Unhandled rejection: ${described.message}`, described.detail);
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);

  const { console: target_console } = target;
  const originalError = target_console.error;
  const originalWarn = target_console.warn;
  target_console.error = (...args: unknown[]) => {
    const { message, detail } = formatConsoleArgs(args);
    debugLog.error("console", message, detail);
    originalError.apply(target_console, args);
  };
  target_console.warn = (...args: unknown[]) => {
    const { message, detail } = formatConsoleArgs(args);
    debugLog.warn("console", message, detail);
    originalWarn.apply(target_console, args);
  };

  uninstall = () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
    target_console.error = originalError;
    target_console.warn = originalWarn;
    uninstall = null;
  };
  return uninstall;
}

declare const __KOLA_VERSION__: string | undefined;

/** The build's version — "dev" under Vite dev and in tests, where the define is absent. */
export function clientVersion(): string {
  return typeof __KOLA_VERSION__ === "string" ? __KOLA_VERSION__ : "dev";
}

/** The `/b/:boardId` on screen, or null off a board. */
export function boardIdFromPath(pathname: string): string | null {
  const match = /^\/b\/([^/?#]+)/.exec(pathname);
  return match?.[1] ?? null;
}

/** Fold the repeat count into the message — the DTO has no `count`. */
export function toDto(entry: DebugLogEntry): DebugLogEntryDto {
  const message = entry.count > 1 ? `${entry.message} (×${entry.count})` : entry.message;
  return {
    ts: entry.ts,
    level: entry.level,
    source: entry.source,
    message: clip(message, DEBUG_REPORT_MAX_MESSAGE),
    ...(entry.detail === undefined ? {} : { detail: entry.detail }),
  };
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, "0");
}

/** Local wall-clock time with milliseconds — what the panel and the copy show. */
export function formatDebugTime(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

/** Plain-text rendering for "Copy" — pasteable into a chat or an email. */
export function formatDebugLog(
  entries: readonly DebugLogEntry[],
  context: { url: string; version: string; userAgent: string },
): string {
  const lines = [
    `Kola debug log — v${context.version} — ${context.url}`,
    context.userAgent,
    "",
  ];
  for (const entry of entries) {
    const dto = toDto(entry);
    lines.push(
      `[${formatDebugTime(entry.ts)}] ${entry.level.toUpperCase().padEnd(5)} ${entry.source.padEnd(8)} ${dto.message}`,
    );
    if (entry.detail !== undefined) {
      for (const detailLine of entry.detail.split("\n")) lines.push(`    ${detailLine}`);
    }
  }
  return lines.join("\n");
}
